// Tracking Utils
import { getWhatsappAuthLinkPageTypeAndSchema } from '@shopee-user/business-customization-logic';

// Constants & Enums
import { WhatsappAuthLinkTrackingPoint } from '@shopee-user/tracking-constants';
import { TrackingOperation } from '@shopee-user/tracking';

// Types
import { TrackingInfo, ImpressionTrackingInfo } from '@shopee-user/tracking';
import { TrackingDataVerifyWhatsappAuthLinkProps } from './types';
import { OtpType } from '../logic';

export function generateTrackingWhatsappAuthLinkPageView({
  operation_number,
  previous_verification_channel,
}: TrackingDataVerifyWhatsappAuthLinkProps): TrackingInfo<
  TrackingDataVerifyWhatsappAuthLinkProps
> {
  return {
    operation: TrackingOperation.View,
    data: {
      operation_number,
      previous_verification_channel,
    },
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.PageView
    ),
  };
}

export function generateTrackingWhatsappAuthLinkOpenWhatsappClick(): TrackingInfo<{}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'open_whatsapp',
    data: {},
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkOpenWhatsappClick
    ),
  };
}

export function generateTrackingWhatsappAuthLinkHelpCenterClick(): TrackingInfo<{}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'help_center',
    data: {},
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkHelpClick
    ),
  };
}

export function generateTrackingWhatsappAuthLinkBackButtonClick(): TrackingInfo<{}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'back_button',
    data: {},
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkBackButton
    ),
  };
}

export function generateTrackingWhatsappAuthLinkResendImpression(): ImpressionTrackingInfo<{}> {
  return {
    operation: TrackingOperation.Impression,
    target_type: 'resend_wa_auth_link',
    data: {},
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.ResendWhatsappAuthLinkImpression
    ),
  };
}

export function generateTrackingWhatsappAuthLinkResendClick(): TrackingInfo<{}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'resend_wa_auth_link',
    data: {},
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.ResendWhatsappAuthLinkImpression
    ),
  };
}

export function generateTrackingWhatsappAuthLinkTryAnotherMethodImpression(): ImpressionTrackingInfo<{}> {
  return {
    operation: TrackingOperation.Impression,
    target_type: 'try_another_method',
    data: {},
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkTryAnotherMethodImpression
    ),
  };
}

export function generateTrackingWhatsappAuthLinkTryAnotherMethodClick(): TrackingInfo<{}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'try_another_method',
    data: {},
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkTryAnotherMethodClick
    ),
  };
}

export function generateTrackingWhatsappAuthLinkLoginWithOtherMethodImpression({
  operation_number,
  previous_verification_channel = '0',
}: TrackingDataVerifyWhatsappAuthLinkProps): ImpressionTrackingInfo<
  TrackingDataVerifyWhatsappAuthLinkProps
> {
  return {
    operation: TrackingOperation.Impression,
    target_type: 'login_with_other_methods',
    data: {
      operation_number,
      previous_verification_channel,
    },
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkLoginWithOtherMethodImpression
    ),
  };
}

export function generateTrackingWhatsappAuthLinkLoginWithOtherMethodClick({
  operation_number,
  previous_verification_channel = '0',
}: TrackingDataVerifyWhatsappAuthLinkProps): TrackingInfo<
  TrackingDataVerifyWhatsappAuthLinkProps
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_other_methods',
    data: {
      operation_number,
      previous_verification_channel,
    },
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkLoginWithOtherMethodsClick
    ),
  };
}

export function generateTrackingWhatsappAuthLinkLoginWithPhoneImpression({
  operation_number,
  previous_verification_channel = '0',
}: TrackingDataVerifyWhatsappAuthLinkProps): ImpressionTrackingInfo<
  TrackingDataVerifyWhatsappAuthLinkProps
> {
  return {
    operation: TrackingOperation.Impression,
    target_type: 'login_with_phone',
    page_section: 'login_with_other_methods_selection',
    data: {
      operation_number,
      previous_verification_channel,
    },
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkLoginWithPhoneImpression
    ),
  };
}

export function generateTrackingWhatsappAuthLinkLoginWithPhoneClick({
  operation_number,
  previous_verification_channel = '0',
}: TrackingDataVerifyWhatsappAuthLinkProps): TrackingInfo<
  TrackingDataVerifyWhatsappAuthLinkProps
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_phone',
    page_section: 'login_with_other_methods_selection',
    data: {
      operation_number,
      previous_verification_channel,
    },
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkLoginWithPhoneClick
    ),
  };
}

export function generateTrackingWhatsappAuthLinkLoginWithBiometricsImpression({
  operation_number,
  previous_verification_channel = '0',
}: TrackingDataVerifyWhatsappAuthLinkProps): ImpressionTrackingInfo<
  TrackingDataVerifyWhatsappAuthLinkProps
> {
  return {
    operation: TrackingOperation.Impression,
    target_type: 'login_with_biometrics',
    page_section: 'login_with_other_methods_selection',
    data: {
      operation_number,
      previous_verification_channel,
    },
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkLoginWithBiometricsImpression
    ),
  };
}

export function generateTrackingWhatsappAuthLinkLoginWithBiometricsClick({
  operation_number,
  previous_verification_channel = '0',
}: TrackingDataVerifyWhatsappAuthLinkProps): TrackingInfo<
  TrackingDataVerifyWhatsappAuthLinkProps
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_biometrics',
    page_section: 'login_with_other_methods_selection',
    data: {
      operation_number,
      previous_verification_channel,
    },
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkLoginWithBiometricsClick
    ),
  };
}

export function generateTrackingWhatsappAuthLinkLoginWithPasswordImpression({
  operation_number,
}: Omit<
  TrackingDataVerifyWhatsappAuthLinkProps,
  'previous_verification_channel'
>): ImpressionTrackingInfo<
  Omit<TrackingDataVerifyWhatsappAuthLinkProps, 'previous_verification_channel'>
> {
  return {
    operation: TrackingOperation.Impression,
    target_type: 'login_with_password',
    page_section: 'login_with_other_methods_selection',
    data: {
      operation_number,
    },
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkLoginWithPasswordImpression
    ),
  };
}

export function generateTrackingWhatsappAuthLinkLoginWithPasswordClick({
  operation_number,
}: Omit<
  TrackingDataVerifyWhatsappAuthLinkProps,
  'previous_verification_channel'
>): TrackingInfo<
  Omit<TrackingDataVerifyWhatsappAuthLinkProps, 'previous_verification_channel'>
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_password',
    page_section: 'login_with_other_methods_selection',
    data: {
      operation_number,
    },
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkLoginWithPasswordClick
    ),
  };
}

export function generateTrackingWhatsappAuthLinkLoginWithQuickLoginClick({
  operation_number,
}: Omit<
  TrackingDataVerifyWhatsappAuthLinkProps,
  'previous_verification_channel'
>): TrackingInfo<
  Omit<TrackingDataVerifyWhatsappAuthLinkProps, 'previous_verification_channel'>
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'quick_login',
    page_section: 'login_with_other_methods_selection',
    data: {
      operation_number,
    },
    ...getWhatsappAuthLinkPageTypeAndSchema(
      WhatsappAuthLinkTrackingPoint.WhatsappAuthLinkLoginWithQuickLoginClick
    ),
  };
}

export function converOtpTypeToPhoneVerificationChannel(otpType: OtpType) {
  switch (otpType) {
    case OtpType.Email:
      return 'SEND_EMAIL_OTP';
    case OtpType.Sms:
      return 'SEND_SMS_OTP';
    case OtpType.Voice:
      return 'SEND_VOICE_OTP';
    case OtpType.WhatsApp:
      return 'SEND_WHATSAPP_OTP';
    case OtpType.Zalo:
      return 'SEND_ZALO_OTP';
    case OtpType.Viber:
      return 'SEND_VIBER_OTP';
    case OtpType.WhatsappAuthLink:
      return 'SEND_WAL';
    default:
      return undefined;
  }
}
